import { apiUrl } from "../../api"
import { beginHistoryRequestTiming, historyUtf8ByteLength } from "./historyPerformance"
import { mapEcSampleContextPrivateV2 } from "./AeroponicEcContextPrivateV2Consumer"

let feederSession = null
let feederLoginOwner = null

export class VirtualFarmerApiError extends Error {
     constructor(message, status) {
          super(message)
          this.name = "VirtualFarmerApiError"
          this.status = status
     }
}

const responseError = async (response, fallback = "Il servizio non è disponibile in questo momento.") => {
     let message = fallback
     try {
          const body = await response.json()
          message = body.message || body.detail || message
     } catch (_) { /* Keep the farmer-friendly fallback. */ }
     return new VirtualFarmerApiError(message, response.status)
}

const statusFamily = (status) => {
     if (!Number.isInteger(status)) return "other"
     const family = Math.floor(status / 100)
     return [2, 4, 5].includes(family) ? `${family}xx` : "other"
}

const errorFromBody = (body, status, fallback = "Il servizio non è disponibile in questo momento.") => new VirtualFarmerApiError(
     body && typeof body === "object" ? body.message || body.detail || fallback : fallback,
     status,
)

const readTimedJson = async (response, timer, report) => {
     const outcome = response.ok ? "success" : "http_error"
     const family = statusFamily(response.status)
     if (typeof response.text !== "function") {
          try {
               const body = await response.json()
               report(timer.finishCombined(outcome, family))
               return body
          } catch (error) {
               report(timer.finishCombined(response.ok ? "invalid_json" : "http_error", family))
               if (response.ok) throw error
               return null
          }
     }

     const text = await response.text()
     timer.markDownloaded(historyUtf8ByteLength(text))
     try {
          const body = JSON.parse(text)
          report(timer.finishSplit(outcome, family))
          return body
     } catch (error) {
          report(timer.finishSplit(response.ok ? "invalid_json" : "http_error", family))
          if (response.ok) throw error
          return null
     }
}

const performRequest = async (path, options = {}, extraHeaders = {}) => {
     const issuedSession = feederSession
     const issuedOwner = feederLoginOwner
     const { historyPerformance = null, expectedStatus = null, ...fetchOptions } = options || {}
     const timer = beginHistoryRequestTiming(historyPerformance)
     const onSample = typeof historyPerformance?.onSample === "function" ? historyPerformance.onSample : null
     let reported = false
     const report = (sample) => {
          if (reported || !sample) return
          reported = true
          try {
               onSample?.(sample)
          } catch (_) { /* Observability is best-effort and must never change request semantics. */ }
     }
     let response
     try {
          response = await fetch(apiUrl(path), {
               ...fetchOptions,
               credentials: "include",
               headers: { "Content-Type": "application/json", ...extraHeaders, ...(fetchOptions.headers || {}) },
          })
          // A late response from a cancelled view must not expire the current session.
          if (fetchOptions.signal?.aborted) throw new DOMException("Request cancelled", "AbortError")
          timer?.markHeaders(response.headers?.get?.("X-Feeder-Request-Id"))
     } catch (error) {
          report(timer?.fail(error?.name === "AbortError" ? "aborted" : "network_error", "none"))
          throw error
     }
     if (response.status === 401 && feederSession === issuedSession && feederLoginOwner === issuedOwner && typeof window !== "undefined") {
          window.dispatchEvent(new Event("feeder-session-expired"))
     }
     if (response.ok && expectedStatus !== null && response.status !== expectedStatus) {
          throw new VirtualFarmerApiError("Request outcome unconfirmed", response.status)
     }
     if (!timer) {
          if (!response.ok) throw await responseError(response)
          return response.json()
     }
     let body
     try {
          body = await readTimedJson(response, timer, report)
     } catch (error) {
          report(timer.fail(error?.name === "AbortError" ? "aborted" : "network_error", statusFamily(response.status)))
          throw error
     }
     if (!response.ok) throw errorFromBody(body, response.status)
     return body
}

const feederRequest = (path, options = {}) => performRequest(path, options)

export const loadFeederSession = async ({ signal } = {}) => {
     if (signal?.aborted) throw new DOMException("Request cancelled", "AbortError")
     const issuedSession = feederSession
     const operation = {}
     feederLoginOwner = operation
     const loaded = await aeroponicRead("/api/v1/auth/me", { method: "GET", signal })
     if (signal?.aborted) throw new DOMException("Request cancelled", "AbortError")
     // A newer explicit auth operation supersedes this read even if it fails.
     // Reference plus owner also rejects a confirmed Logout-to-null ABA.
     if (feederLoginOwner !== operation || feederSession !== issuedSession) {
          throw new VirtualFarmerApiError("Request outcome unconfirmed", 200)
     }
     feederSession = loaded
     feederLoginOwner = null
     return feederSession
}

export const loginFeeder = async (email, password) => {
     const issuedSession = feederSession
     const operation = {}
     feederLoginOwner = operation
     const authenticated = await feederRequest("/api/v1/auth/login", {
          method: "POST",
          body: JSON.stringify({ email, password }),
     })
     // Local ownership only: never undo a newer login or confirmed session
     // read/clear, even if the original null cache reference reappeared (ABA).
     if (feederLoginOwner !== operation || feederSession !== issuedSession) {
          throw new VirtualFarmerApiError("Request outcome unconfirmed", 200)
     }
     feederSession = authenticated
     return authenticated
}

export const logoutFeeder = async () => {
     const issuedSession = feederSession
     if (!issuedSession?.csrf_token) return
     const acknowledgement = await feederRequest("/api/v1/auth/logout", {
          method: "POST",
          headers: { "X-CSRF-Token": issuedSession.csrf_token },
          expectedStatus: 200,
     })
     if (!acknowledgement || Object.getPrototypeOf(acknowledgement) !== Object.prototype
          || Reflect.ownKeys(acknowledgement).length !== 1 || Reflect.ownKeys(acknowledgement)[0] !== "ok" || acknowledgement.ok !== true) {
          throw new VirtualFarmerApiError("Request outcome unconfirmed", 200)
     }
     if (feederSession === issuedSession) {
          feederSession = null
          feederLoginOwner = null
     }
     return true
}

const request = (path, options = {}) => {
     const method = String(options.method || "GET").toUpperCase()
     const csrfHeader = !["GET", "HEAD", "OPTIONS"].includes(method) && feederSession?.csrf_token
          ? { "X-CSRF-Token": feederSession.csrf_token }
          : {}
     return performRequest(path, options, csrfHeader)
}

const jsonMutation = (path, method, body) => request(path, { method, body: JSON.stringify(body) })

export const loadVirtualFarmerPortal = (options) => {
     if (options === undefined) return request("/api/virtual-farmer/portal")
     return aeroponicRead("/api/virtual-farmer/portal", { method: "GET", signal: options.signal })
}
export const loadVirtualFarmerLocationWeather = (locationId) => request(`/api/virtual-farmer/locations/${encodeURIComponent(locationId)}/weather`)
export const loadVirtualFarmerIrrigationHistory = (locationId, range) => request(
     `/api/virtual-farmer/locations/${encodeURIComponent(locationId)}/irrigation-history?range=${encodeURIComponent(range)}`,
)
export const saveVirtualFarmerSetup = (setup) => jsonMutation("/api/virtual-farmer/setup", "POST", setup)
export const addVirtualFarmerLocation = (location) => jsonMutation("/api/virtual-farmer/locations", "POST", location)
export const updateVirtualFarmerLocation = (id, location) => jsonMutation(`/api/virtual-farmer/locations/${id}`, "PUT", location)
export const deleteVirtualFarmerLocation = (id) => request(`/api/virtual-farmer/locations/${id}`, { method: "DELETE" })
export const addVirtualFarmerSensor = (sensor) => jsonMutation("/api/virtual-farmer/sensors", "POST", sensor)
export const setVirtualFarmerSensorEnabled = (id, enabled) => jsonMutation(`/api/virtual-farmer/sensors/${id}/enabled`, "PUT", { enabled })
export const updateVirtualFarmerSensorCredentials = (id, credential) => jsonMutation(`/api/virtual-farmer/sensors/${id}/credentials`, "PUT", credential)
export const deleteVirtualFarmerSensor = (id) => request(`/api/virtual-farmer/sensors/${id}`, { method: "DELETE" })
export const collectVirtualFarmerSensor = (id) => request(`/api/virtual-farmer/sensors/${id}/collect`, { method: "POST" })
export const addVirtualFarmerChuck = (chuck) => jsonMutation("/api/virtual-farmer/chucks", "POST", chuck)
export const deleteVirtualFarmerChuck = (id) => request(`/api/virtual-farmer/chucks/${encodeURIComponent(id)}`, { method: "DELETE" })
export const linkVirtualFarmerChuckSource = (id, source) => jsonMutation(
     `/api/virtual-farmer/chucks/${encodeURIComponent(id)}/feeder-source`,
     "PUT",
     source,
)
export const addVirtualFarmerActuator = (actuator) => jsonMutation("/api/virtual-farmer/actuators", "POST", actuator)
export const refreshVirtualFarmerActuator = (id) => request(`/api/virtual-farmer/actuators/${id}/refresh`, { method: "POST" })
export const deleteVirtualFarmerActuator = (id) => request(`/api/virtual-farmer/actuators/${id}`, { method: "DELETE" })
export const configureVirtualFarmerPump = (id, configuration) => jsonMutation(`/api/virtual-farmer/actuators/${id}/pump-configuration`, "PUT", configuration)
export const testVirtualFarmerPumpPulse = (id) => request(`/api/virtual-farmer/actuators/${id}/supervised-test-pulse`, { method: "POST" })
export const confirmVirtualFarmerPumpTest = (id) => jsonMutation(`/api/virtual-farmer/actuators/${id}/confirm-manual-test`, "POST", { confirmed: true })
export const releaseVirtualFarmerLocalController = (id) => jsonMutation(`/api/virtual-farmer/actuators/${id}/confirm-local-controller-released`, "POST", { confirmed: true })
export const commandVirtualFarmerPump = (id, command) => jsonMutation(`/api/virtual-farmer/actuators/${id}/command`, "POST", command)
export const loadVirtualFarmerActuatorAudit = (id) => request(`/api/virtual-farmer/actuators/${id}/audit`)
export const loadVirtualFarmerSensorReadings = (id, from, to, options = {}) => request(
     `/api/virtual-farmer/sensors/${encodeURIComponent(id)}/readings?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
     options,
)
const SENSOR_HISTORY_RANGE_MILLISECONDS = {
     "24h": 24 * 60 * 60 * 1000,
     "7d": 7 * 24 * 60 * 60 * 1000,
     "30d": 30 * 24 * 60 * 60 * 1000,
}

const withHistoryPerformance = (options, descriptor) => {
     const { onHistoryPerformance = null, historyPerformance: _ignoredHistoryPerformance, ...fetchOptions } = options || {}
     return {
          ...fetchOptions,
          historyPerformance: typeof onHistoryPerformance === "function"
               ? { ...descriptor, onSample: onHistoryPerformance }
               : null,
     }
}

export const loadVirtualFarmerSensorHistory = async (id, range = "24h", to = new Date().toISOString(), options = {}) => {
     const normalizedRange = Object.prototype.hasOwnProperty.call(SENSOR_HISTORY_RANGE_MILLISECONDS, range) ? range : "24h"
     const end = new Date(to)
     if (!Number.isFinite(end.getTime())) throw new TypeError("A valid history end timestamp is required")
     try {
          return await request(
               `/api/virtual-farmer/sensors/${encodeURIComponent(id)}/history?range=${encodeURIComponent(normalizedRange)}&to=${encodeURIComponent(end.toISOString())}`,
               withHistoryPerformance(options, { operation: "sensor_history", range: normalizedRange, leg: "aggregate" }),
          )
     } catch (error) {
          if (!(error instanceof VirtualFarmerApiError) || ![404, 405].includes(error.status)) throw error
          const from = new Date(end.getTime() - SENSOR_HISTORY_RANGE_MILLISECONDS[normalizedRange]).toISOString()
          return loadVirtualFarmerSensorReadings(
               id,
               from,
               end.toISOString(),
               withHistoryPerformance(options, { operation: "sensor_history", range: normalizedRange, leg: "legacy" }),
          )
     }
}
export const searchVirtualFarmerPlaces = ({ place, region, countryCode, language }, { signal } = {}) => aeroponicRead(
     `/api/virtual-farmer/geocoding/places?place=${encodeURIComponent(place)}&region=${encodeURIComponent(region || "")}&countryCode=${encodeURIComponent(countryCode || "AT")}&language=${encodeURIComponent(language || "de")}`,
     { method: "GET", signal },
)

export const AEROPONIC_READ_TIMEOUT_MS = 15000

// Bound both the headers and body read. Race cancellation as well as aborting
// fetch so even a transport that ignores AbortSignal cannot keep the UI waiting.
const aeroponicRead = async (path, options = {}) => {
     const { signal, ...fetchOptions } = options
     if (signal?.aborted) throw new DOMException("Request cancelled", "AbortError")
     const controller = new AbortController()
     let cancel
     const deadline = new Promise((_, reject) => { cancel = reject })
     const abort = () => {
          cancel(new DOMException("Request cancelled", "AbortError"))
          controller.abort()
     }
     signal?.addEventListener("abort", abort, { once: true })
     const timeout = setTimeout(() => {
          cancel(new DOMException("Aeroponic read timed out", "TimeoutError"))
          controller.abort()
     }, AEROPONIC_READ_TIMEOUT_MS)
     try {
          return await Promise.race([
               feederRequest(path, { ...fetchOptions, cache: "no-store", signal: controller.signal }),
               deadline,
          ])
     } finally {
          clearTimeout(timeout)
          signal?.removeEventListener("abort", abort)
     }
}

export const loadFeederDevices = (options = {}) => aeroponicRead("/api/v1/devices", options)
export const claimFeederDevice = (claimCode, displayName) => jsonMutation(
     "/api/v1/onboarding/claim",
     "POST",
     { claim_code: claimCode, display_name: displayName },
)
export const CHAAC_CLAIM_TIMEOUT_MS = 15000
export const claimChaacDevice = async (claimCode, displayName) => {
     const controller = new AbortController()
     let timeout
     const deadline = new Promise((_, reject) => {
          timeout = setTimeout(() => {
               reject(new DOMException("Claim outcome unconfirmed", "TimeoutError"))
               controller.abort()
          }, CHAAC_CLAIM_TIMEOUT_MS)
     })
     try {
          // The write may have committed despite a lost response. Never retry
          // a claim automatically, and bound body reads as well as headers.
          return await Promise.race([request("/api/v1/research/chaac-pairing/claim", {
               method: "POST", cache: "no-store", signal: controller.signal,
               body: JSON.stringify({ claim_code: claimCode, name: displayName }),
          }), deadline])
     } finally { clearTimeout(timeout) }
}
export const loadAeroponicDashboard = (deviceId, options = {}) => aeroponicRead(
     `/api/v1/dashboard${deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : ""}`,
     { cache: "no-store", ...options },
)

export const loadAeroponicEcSampleContext = (sampleRowId, { signal } = {}) => {
     if (!Number.isSafeInteger(sampleRowId) || sampleRowId <= 0) throw new TypeError("A safe positive sample id is required")
     return aeroponicRead(`/api/v1/samples/${sampleRowId}/ec-context`, { signal, method: "GET" })
}

export const loadAeroponicEcProfileCandidates = (sampleRowId, { signal } = {}) => {
     if (!Number.isSafeInteger(sampleRowId) || sampleRowId <= 0) throw new TypeError("A safe positive sample id is required")
     return aeroponicRead(`/api/v1/samples/${sampleRowId}/ec-profile-candidates`, { signal, method: "GET" })
}

export const loadAeroponicEcProfileCoherence = (sampleRowId, profileRecordIds, { signal } = {}) => {
     if (!Number.isSafeInteger(sampleRowId) || sampleRowId <= 0 || !Array.isArray(profileRecordIds) || profileRecordIds.length > 2) throw new TypeError("Exact original profile IDs are required")
     const ids = Array.from(profileRecordIds)
     if (ids.length > 2 || !ids.every(value => typeof value === "string" && /^[1-9][0-9]{0,18}$/.exec(value)?.[0] === value
               && (value.length < 19 || value <= "9223372036854775807"))) throw new TypeError("Exact original profile IDs are required")
     const query = new URLSearchParams()
     for (const pk of new Set(ids)) query.append("profile_record_id", pk)
     const suffix = query.toString()
     return aeroponicRead(`/api/v1/samples/${sampleRowId}/ec-profile-coherence${suffix ? `?${suffix}` : ""}`, { signal, method: "GET" })
}

const AEROPONIC_HISTORY_RANGES = new Set(["24h", "7d", "30d"])

export const loadAeroponicPassiveHistory = (deviceId, range = "24h", options = {}) => {
     if (!String(deviceId || "").trim()) throw new TypeError("An Aeroponic device id is required")
     if (!["1h", "24h", "7d", "30d"].includes(range)) throw new TypeError("Invalid passive history range")
     return aeroponicRead(`/api/v1/devices/${encodeURIComponent(deviceId)}/passive-history?range=${range}`, options)
}

export const loadAeroponicTimescaleHistory = async (deviceId, range = "24h", options = {}) => {
     const normalizedDeviceId = String(deviceId || "").trim()
     if (!normalizedDeviceId) throw new TypeError("An Aeroponic device id is required")
     if (!AEROPONIC_HISTORY_RANGES.has(range)) throw new TypeError("Aeroponic history range must be 24h, 7d or 30d")
     try {
          const history = await aeroponicRead(
               `/api/v1/devices/${encodeURIComponent(normalizedDeviceId)}/aeroponic-history?range=${encodeURIComponent(range)}`,
               { cache: "no-store", ...options },
          )
          return { mode: "timescale", history }
     } catch (error) {
          if (!(error instanceof VirtualFarmerApiError) || error.status !== 409) throw error
          return { mode: "awaiting_new_data" }
     }
}

export const RESEARCH_REQUEST_TIMEOUT_MS = 15000
const boundedResearchRequest = async (path, options = {}) => {
     const { signal, ...fetchOptions } = options
     if (signal?.aborted) throw new DOMException("Request cancelled", "AbortError")
     const controller = new AbortController()
     let cancel
     const deadline = new Promise((_, reject) => { cancel = reject })
     const abort = () => {
          cancel(new DOMException("Request cancelled", "AbortError"))
          controller.abort()
     }
     signal?.addEventListener("abort", abort, { once: true })
     const timeout = setTimeout(() => {
          cancel(new DOMException("Research request outcome unconfirmed", "TimeoutError"))
          controller.abort()
     }, RESEARCH_REQUEST_TIMEOUT_MS)
     try {
          // Mutations retain session CSRF. Abort/timeout is not proof that a
          // write failed: callers must refresh, never retry automatically.
          return await Promise.race([
               request(path, { ...fetchOptions, cache: "no-store", signal: controller.signal }),
               deadline,
          ])
     } finally {
          clearTimeout(timeout)
          signal?.removeEventListener("abort", abort)
     }
}
export const loadResearchOverview = (options = {}) => boundedResearchRequest("/api/v1/research/overview", options)
export const loadResearchAccountRuns = (options = {}) => boundedResearchRequest("/api/v1/research/account-runs", options)
export const normalizeAccountRunStart = value => {
     if (typeof value !== "string" || value.length > 40) return null
     const match = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.\d{1,6})?(Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/.exec(value)
     if (!match || Number(value.slice(0, 4)) < 1) return null
     const wall = Date.parse(`${match[1]}Z`)
     const moment = Date.parse(value)
     if (!Number.isFinite(wall) || !Number.isFinite(moment) || new Date(wall).toISOString().slice(0, 19) !== match[1]) return null
     const utc = new Date(moment).toISOString()
     return /^\d{4}-/.test(utc) && Number(utc.slice(0, 4)) >= 1 ? utc : null
}
export const createResearchAccountRun = (draft, options = {}) => {
     const name = typeof draft?.name === "string" ? draft.name.trim() : ""
     const location = draft?.external_location_id
     const startedAt = normalizeAccountRunStart(draft?.started_at)
     if (!name || [...name].length > 160 || typeof location !== "string" || !location || location !== location.trim() || [...location].length > 160 || !startedAt) {
          return Promise.reject(new TypeError("A run name, exact saved location ID and timezone-qualified start are required"))
     }
     // Only these three scope fields cross the boundary. Account, status,
     // devices, bindings, headers and control settings cannot be supplied.
     return boundedResearchRequest("/api/v1/research/account-runs", {
          method: "POST", signal: options.signal, expectedStatus: 201,
          body: JSON.stringify({ name, external_location_id: location, started_at: startedAt }),
     })
}
export const assignResearchDeploymentAccountRun = (deploymentId, accountRunId, options = {}) => {
     if (!Number.isSafeInteger(deploymentId) || deploymentId <= 0 || !Number.isSafeInteger(accountRunId) || accountRunId <= 0) {
          return Promise.reject(new TypeError("Exact positive deployment and account run IDs are required"))
     }
     return boundedResearchRequest(`/api/v1/research/deployments/${deploymentId}/account-run`, {
          method: "POST", signal: options.signal,
          body: JSON.stringify({ account_run_id: accountRunId }),
     })
}
export const loadResearchChuckTelemetry = (nodePublicId, range = "24h", options = {}) => {
     return feederRequest(
          `/api/v1/research/nodes/${encodeURIComponent(nodePublicId)}/telemetry?range=${encodeURIComponent(range)}`,
          withHistoryPerformance(
               { cache: "no-store", ...options },
               { operation: "chaac_log", range, leg: "research" },
          ),
     )
}
export const loadResearchChuckLatestTelemetry = (nodePublicId, options = {}) => feederRequest(
     `/api/v1/research/nodes/${encodeURIComponent(nodePublicId)}/telemetry/latest`,
     { cache: "no-store", ...options },
)
export const loadResearchRoomUniformity = (deploymentId, variableCode, range = "24h", setupVersion = null) => {
     const parameters = new URLSearchParams({
          variable_code: variableCode,
          range,
     })
     if (setupVersion) parameters.set("setup_version", String(setupVersion))
     return feederRequest(
          `/api/v1/research/deployments/${encodeURIComponent(deploymentId)}/room-uniformity?${parameters.toString()}`,
          { cache: "no-store" },
     )
}
export const loadFirmwareNodeStatus = (nodePublicId) => request(
     `/api/v1/firmware/nodes/${encodeURIComponent(nodePublicId)}`,
     { cache: "no-store" },
)
export const assignFirmwareRelease = (nodePublicId, releasePublicId) => jsonMutation(
     `/api/v1/firmware/nodes/${encodeURIComponent(nodePublicId)}/assignments`,
     "POST",
     { release_public_id: releasePublicId },
)
export const saveResearchProfile = (profile) => jsonMutation(
     "/api/v1/research/profile",
     "PUT",
     profile,
)
export const createResearchStudy = (study) => jsonMutation(
     "/api/v1/research/studies",
     "POST",
     study,
)
export const enrollResearchRun = (publicId, enrollment) => jsonMutation(
     `/api/v1/research/studies/${encodeURIComponent(publicId)}/enrollments`,
     "POST",
     enrollment,
)
export const withdrawResearchEnrollment = (enrollmentId) => request(
     `/api/v1/research/enrollments/${encodeURIComponent(enrollmentId)}/withdraw`,
     { method: "POST" },
)
export const createResearchNode = (node) => jsonMutation(
     "/api/v1/research/nodes",
     "POST",
     node,
)
export const addResearchNodeComponent = (nodeId, component) => jsonMutation(
     `/api/v1/research/nodes/${encodeURIComponent(nodeId)}/components`,
     "POST",
     component,
)
export const createResearchDeployment = (deployment) => jsonMutation(
     "/api/v1/research/deployments",
     "POST",
     deployment,
)
export const linkResearchDeploymentNode = (deploymentId, link) => jsonMutation(
     `/api/v1/research/deployments/${encodeURIComponent(deploymentId)}/nodes`,
     "POST",
     link,
)

// ORIGINAL RECORD METADATA TRANSPORT BEGIN
// Private retained integrity metadata only; these flags never grant export,
// retention, calibration, device-clock UTC mapping or physical-history authority.
const originalMetadataDecimal = (value, positive = false) => typeof value === "string"
     && (positive ? /^[1-9][0-9]{0,18}$/ : /^(?:0|[1-9][0-9]{0,18})$/).exec(value)?.[0] === value
     && (value.length < 19 || value <= "9223372036854775807")
const originalMetadataCounter = value => originalMetadataDecimal(value) && value.length <= 10
     && (value.length < 10 || value <= "4294967295")
const originalMetadataKeys = (value, keys) => value !== null && typeof value === "object"
     && Object.getPrototypeOf(value) === Object.prototype
     && Reflect.ownKeys(value).length === keys.length
     && keys.every(key => Object.prototype.hasOwnProperty.call(value, key))
const originalMetadataHash = value => typeof value === "string" && value.length === 64
     && /^[0-9a-f]{64}$/.exec(value)?.[0] === value
const originalMetadataUtc = value => typeof value === "string" && value.length <= 40
     && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|\+00:00)$/.exec(value)?.[0] === value
     && normalizeAccountRunStart(value) !== null
const originalMetadataGrants = ["controls_hardware", "calibration_applied", "ec25_applied",
     "archive_export_authorized", "source_retention_authorized", "complete_physical_history"]
const originalMetadataValid = (value, recordId) => {
     if (!originalMetadataKeys(value, ["version", "visibility", "evidence_kind", "control_domain",
          ...originalMetadataGrants, "selection_state", "source", "wire", "receipt", "device_clock"])
          || value.version !== 1 || value.visibility !== "private"
          || value.evidence_kind !== "retained_original_metadata_integrity" || value.control_domain !== "aeroponic"
          || !originalMetadataGrants.every(key => value[key] === false)
          || !["PRESENT", "CAPTURED_ABSENT"].includes(value.selection_state)) return false
     const { source, wire, receipt, device_clock: clock } = value
     if (!originalMetadataKeys(source, ["record_id", "record_type", "sequence", "device_tick_ms", "cycle_id", "received_at", "content_sha256"])
          || source.record_id !== recordId || !originalMetadataDecimal(source.record_id, true)
          || !["boot", "sample", "cycle", "analysis", "event", "observation", "ec_profile"].includes(source.record_type)
          || !originalMetadataCounter(source.sequence)
          || (source.device_tick_ms !== null && !originalMetadataCounter(source.device_tick_ms))
          || (source.cycle_id !== null && (!Number.isInteger(source.cycle_id) || source.cycle_id < 0 || source.cycle_id > 4294967295))
          || !originalMetadataUtc(source.received_at) || !originalMetadataHash(source.content_sha256)) return false
     if (!originalMetadataKeys(wire, ["ordinal", "body_sha256", "qualification_policy", "field_surface_qualified"])
          || !Number.isInteger(wire.ordinal) || wire.ordinal < 0 || wire.ordinal > 127
          || !originalMetadataHash(wire.body_sha256) || wire.qualification_policy !== "field_surface_v1"
          || typeof wire.field_surface_qualified !== "boolean") return false
     if (!originalMetadataKeys(receipt, ["batch_receipt_id", "received_at", "clock_basis", "clock_uncertainty_ms", "record_count", "inserted_count", "original_member_count"])
          || !originalMetadataDecimal(receipt.batch_receipt_id, true)
          || receipt.received_at !== source.received_at || !originalMetadataUtc(receipt.received_at)
          || !["monotonic_uptime", "source_utc", "server_receipt"].includes(receipt.clock_basis)
          || (receipt.clock_uncertainty_ms !== null && (!Number.isSafeInteger(receipt.clock_uncertainty_ms) || receipt.clock_uncertainty_ms < 0))
          || !Number.isInteger(receipt.record_count) || receipt.record_count < 1 || receipt.record_count > 128
          || !Number.isInteger(receipt.inserted_count) || receipt.inserted_count < 1 || receipt.inserted_count > receipt.record_count
          || !Number.isInteger(receipt.original_member_count) || receipt.original_member_count !== receipt.inserted_count
          || wire.ordinal >= receipt.record_count) return false
     return originalMetadataKeys(clock, ["basis", "utc_mapped"])
          && clock.basis === "monotonic_tick" && clock.utc_mapped === false
}

export const loadAeroponicOriginalRecordMetadata = async (recordId, { signal } = {}) => {
     if (!originalMetadataDecimal(recordId, true)) throw new TypeError("An exact original record id is required")
     if (signal?.aborted) throw new DOMException("Request cancelled", "AbortError")
     const issuedSession = feederSession
     const issuedOwner = feederLoginOwner
     if (!Number.isSafeInteger(issuedSession?.id) || issuedSession.id <= 0
          || typeof issuedSession.csrf_token !== "string" || !issuedSession.csrf_token
          || issuedSession.must_change_password !== false) {
          throw new VirtualFarmerApiError("Original metadata unavailable", issuedSession?.must_change_password === true ? 403 : 401)
     }
     if (feederSession !== issuedSession || feederLoginOwner !== issuedOwner) {
          throw new VirtualFarmerApiError("Request outcome unconfirmed", 200)
     }
     const loaded = await aeroponicRead(`/api/v1/records/${recordId}/original-metadata`, { method: "GET", signal })
     if (signal?.aborted) throw new DOMException("Request cancelled", "AbortError")
     if (feederSession !== issuedSession || feederLoginOwner !== issuedOwner) {
          throw new VirtualFarmerApiError("Request outcome unconfirmed", 200)
     }
     if (!originalMetadataValid(loaded, recordId)) throw new VirtualFarmerApiError("Original metadata unavailable", 200)
     return loaded
}
// ORIGINAL RECORD METADATA TRANSPORT END
export const createResearchSetupVersion = (deploymentId, setup) => jsonMutation(
     `/api/v1/research/deployments/${encodeURIComponent(deploymentId)}/setup-versions`,
     "POST",
     setup,
)

// EC CONTEXT PRIVATE V2 TRANSPORT BEGIN
// Explicit private compat read only. Local cache/scope coherence is not cookie,
// captured ACL, calibration, physical-history or archive authority.
export const loadAeroponicEcSampleContextPrivateV2 = async (selectedSampleId, selectedDeviceId, { signal } = {}) => {
     mapEcSampleContextPrivateV2(null, selectedSampleId, selectedDeviceId)
     if (signal?.aborted) throw new DOMException("Request cancelled", "AbortError")
     const issuedSession = feederSession
     const issuedOwner = feederLoginOwner
     const issuedAccountId = issuedSession?.id
     if (!Number.isSafeInteger(issuedAccountId) || issuedAccountId <= 0
          || typeof issuedSession.csrf_token !== "string" || !issuedSession.csrf_token
          || issuedSession.must_change_password !== false) {
          throw new VirtualFarmerApiError("EC context v2 unavailable", issuedSession?.must_change_password === true ? 403 : 401)
     }
     if (feederSession !== issuedSession || feederLoginOwner !== issuedOwner) {
          throw new VirtualFarmerApiError("Request outcome unconfirmed", 200)
     }
     let loaded
     try {
          loaded = await aeroponicRead(`/api/v1/samples/${selectedSampleId}/ec-context-v2`, {
               method: "GET", signal, expectedStatus: 200, redirect: "error",
          })
     } catch (error) {
          if (signal?.aborted) throw new DOMException("Request cancelled", "AbortError")
          if (error instanceof SyntaxError) throw new VirtualFarmerApiError("EC context v2 unavailable", 200)
          throw error
     }
     if (signal?.aborted) throw new DOMException("Request cancelled", "AbortError")
     if (feederSession !== issuedSession || feederLoginOwner !== issuedOwner) {
          throw new VirtualFarmerApiError("Request outcome unconfirmed", 200)
     }
     let view
     try {
          view = mapEcSampleContextPrivateV2(loaded, selectedSampleId, selectedDeviceId)
     } catch (error) {
          if (!(error instanceof TypeError)) throw error
          throw new VirtualFarmerApiError("EC context v2 unavailable", 200)
     }
     if (view === null || loaded.captured_scope.account_id !== String(issuedAccountId)) {
          throw new VirtualFarmerApiError("EC context v2 unavailable", 200)
     }
     return view
}
// EC CONTEXT PRIVATE V2 TRANSPORT END
